import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function FAQ() {
  const [openSection, setOpenSection] = useState<string | null>('getting-started');

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  const faqSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      questions: [
        {
          question: 'What is SellSim?',
          answer: 'SellSim is an AI-powered sales training and simulation platform developed by a seasoned sales consultant with over 30 years of global experience. It combines human-designed frameworks, audio training, and quizzes with adaptive AI role-play to help teams practise, improve, and master real-world sales conversations.'
        },
        {
          question: 'How does SellSim\'s AI sales training work?',
          answer: 'SellSim researches your company, products, and market. It then builds custom training modules and AI-driven role-plays that mirror your sales environment. Reps practise conversations with virtual buyers, receive instant feedback, and managers track performance via dashboards.'
        },
        {
          question: 'Who is SellSim designed for?',
          answer: 'It\'s built for sales teams — from enterprise to fast-growth SMEs — who want training that\'s company-specific, measurable, and scalable.'
        },
        {
          question: 'How do I get started after signing up?',
          answer: 'Once you register, you\'ll complete a short onboarding form or join a discovery workshop. SellSim uses this information to design your company-specific framework and training modules, including role play scenarios.'
        },
        {
          question: 'What happens after I complete the onboarding form or book a call?',
          answer: 'You\'ll receive confirmation and access to your project workspace. Within 2 working days, you\'ll get your first custom training modules and AI simulations.'
        },
        {
          question: 'How long does it take to build my custom training framework?',
          answer: 'Typically 2 working days after onboarding.'
        }
      ]
    },
    {
      id: 'company-programme',
      title: 'Company & Programme Details',
      questions: [
        {
          question: 'Who created SellSim?',
          answer: 'Find out more about our founders on our <a href="https://sellsim.co.uk/about" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">about page</a>.'
        },
        {
          question: 'What makes SellSim different from other sales training providers?',
          answer: 'It combines 30 years of human consulting experience with adaptive AI, building company-specific simulations that deliver measurable, behavioural change — not theory. This learning is cemented into the delegate\'s mind with relevant practise in the form of roleplay.'
        },
        {
          question: 'Do you offer certification or CPD accreditation?',
          answer: 'Not yet, but successful delegates will receive a score for their efforts.'
        },
        {
          question: 'Do you work internationally?',
          answer: 'Yes — SellSim operates globally, supporting teams in the UK, Europe, North America, and Asia-Pacific regions.'
        }
      ]
    },
    {
      id: 'payments-subscriptions',
      title: 'Payments & Subscriptions',
      questions: [
        {
          question: 'What does it cost?',
          answer: 'We are an early stage startup with a small team, so offering specific pricing for launch, we have a very simple model. Price on application. Flat rate per month dependent on the size of your team. *Minimum duration of three months. Discounts available for clients wishing to commit to 12 months or more. A fair usage cap will be applied to the total duration of the roleplay session.'
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'Major credit/debit cards via Stripe or bank transfer.'
        },
        {
          question: 'When will I be charged?',
          answer: 'You\'re charged when you activate your subscription or approve your project proposal.'
        },
        {
          question: 'Do you offer monthly or annual plans?',
          answer: 'Yes — both. Annual plans include discounted pricing.'
        },
        {
          question: 'Is there a free trial?',
          answer: 'SellSim doesn\'t offer a public free trial, but please ask for a demo for more information.'
        },
        {
          question: 'Do you issue VAT invoices?',
          answer: 'Yes, UK VAT invoices are automatically issued.'
        }
      ]
    },
    {
      id: 'cancellations-refunds',
      title: 'Cancellations & Refunds',
      questions: [
        {
          question: 'How do I cancel my subscription?',
          answer: 'You can cancel anytime after the initial 3 months minimum term. Simply notify your account manager by email.'
        },
        {
          question: 'What happens if I cancel mid-month?',
          answer: 'Your account and course dashboard remain active until the end of the current billing cycle.'
        },
        {
          question: 'Do you offer refunds?',
          answer: 'Refunds are only available in the unlikely case of the SellSim platform not delivering the intended service.'
        },
        {
          question: 'Can I pause my subscription?',
          answer: 'No'
        },
        {
          question: 'What happens to my data if I cancel?',
          answer: 'All data is securely deleted within 30 days unless otherwise requested for compliance or record-keeping.'
        }
      ]
    },
    {
      id: 'accounts-access',
      title: 'Accounts & Access',
      questions: [
        {
          question: 'How do I log in to my account?',
          answer: 'Visit app.sellsim.co.uk/login and enter your registered email and password.'
        },
        {
          question: 'I forgot my password — how do I reset it?',
          answer: 'You will find your login details in your onboarding email, which was sent when your course was first created. If it has been lost, please contact support@sellsim.co.uk'
        },
        {
          question: 'Can I change my email address or contact info?',
          answer: 'Yes — update details in your account settings or contact support for assistance.'
        },
        {
          question: 'How do I add team members to my account?',
          answer: 'Team specific functionality is still in development.'
        },
        {
          question: 'Can multiple users access the same training dashboard?',
          answer: 'Not yet.'
        }
      ]
    },
    {
      id: 'training-content',
      title: 'Training & Content',
      questions: [
        {
          question: 'How is my training personalised?',
          answer: 'SellSim analyses your company\'s website, product range, customer base, and sales structure to build a program that mirrors your real-world environment. In addition, we have an important consultation call where we fill in any gaps in this initial research phase.'
        },
        {
          question: 'Can I upload my own sales materials or case studies?',
          answer: 'Yes. You can provide scripts, case studies, or pitch decks to support our creation of your course.'
        },
        {
          question: 'How do I access my training modules?',
          answer: 'Modules are accessed through the SellSim web dashboard — optimised for desktop usage.'
        },
        {
          question: 'How long do I have access to completed courses?',
          answer: 'Access remains available while your subscription is active.'
        },
        {
          question: 'Can I request additional topics or modules?',
          answer: 'Yes — new modules can be commissioned anytime to align with upcoming campaigns, products, or performance gaps.'
        }
      ]
    },
    {
      id: 'support-communication',
      title: 'Support & Communication',
      questions: [
        {
          question: 'How do I contact the SellSim support team?',
          answer: 'Email support@sellsim.co.uk.'
        },
        {
          question: 'What is your response time?',
          answer: 'Typically, within 24 hours (Monday–Friday).'
        },
        {
          question: 'Can I schedule a live demo or consultation?',
          answer: 'Yes — book directly via the website or through your client dashboard. https://calendly.com/glen-williamson/make-your-strategy-a-reality'
        },
        {
          question: 'How do I provide feedback or suggest improvements?',
          answer: 'Use the feedback button inside your training session or email support@sellsim.co.uk.'
        }
      ]
    },
    {
      id: 'policies-legal',
      title: 'Policies & Legal',
      questions: [
        {
          question: 'How is my company\'s data used in AI research?',
          answer: 'Aggregated, anonymised data may be used to improve simulation quality — never for external research or resale.'
        },
        {
          question: 'Do you share my data with third parties?',
          answer: 'No. Data is never sold or shared. Third-party providers (like payment processors) operate under strict data-processing agreements.'
        },
        {
          question: 'What are your terms of service?',
          answer: 'Full terms are available at sellsim.co.uk/terms.'
        }
      ]
    },
    {
      id: 'technical-integration',
      title: 'Technical & Integration',
      questions: [
        {
          question: 'What browsers and devices are supported?',
          answer: 'Chrome, Edge, Firefox, and Safari on desktop and mobile. Performance is optimised for modern devices.'
        },
        {
          question: 'I\'m having trouble loading the training — what should I do?',
          answer: 'Try refreshing, clearing your browser cache, or checking your internet connection. If issues persist, contact support with screenshots.'
        },
        {
          question: 'Can SellSim integrate with my CRM or LMS?',
          answer: 'Not yet.'
        },
        {
          question: 'Do I need to install any software?',
          answer: 'No installation is required. SellSim runs entirely in your browser.'
        },
        {
          question: 'How do I clear the cache or fix login issues?',
          answer: 'In Chrome, go to Settings → Privacy → Clear Browsing Data, then restart your browser. Use incognito mode to test clean logins.'
        },
        {
          question: 'Do I require any hardware?',
          answer: 'In addition to an online PC, the only hardware required is a microphone and speakers, ideally a headset.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find answers to common questions about SellSim's AI-powered sales training platform, pricing, and getting started.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {faqSections.map((section) => (
              <div key={section.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-8 py-6 text-left bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {section.title}
                    </h2>
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`ri-arrow-${openSection === section.id ? 'up' : 'down'}-s-line text-2xl text-gray-600`}></i>
                    </div>
                  </div>
                </button>
                
                {openSection === section.id && (
                  <div className="px-8 py-6 space-y-6">
                    {section.questions.map((faq, index) => (
                      <div key={index} className="border-b border-gray-100 last:border-b-0 pb-6 last:pb-0">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                          {faq.question}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white p-8 rounded-xl shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-gray-600 mb-8">
              Can't find what you're looking for? Our team is here to help you get started with SellSim.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Contact Us
              </a>
              <a
                href="https://app.sellsim.co.uk/free-sales-strategy"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-teal-600 border-2 border-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Strategy Doc
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
